import React, { useRef, useState } from "react";
import { Row, Col, Input, Button, Card, CardBody, CardHeader, FormGroup, Label, Spinner } from "reactstrap";
import { Formik, Form, FieldArray } from "formik";
import { workoutSchema } from "../../../../models/workout";
import { WORKOUT_STYLES, WORKOUT_INTENSITIES } from "../constants";
import { OverlayLoader } from "../../../../components/Common";
import { useReactToPrint } from "react-to-print";
import { useTenant } from "../../../../hooks/useTenant";
import PrintableWorkout from "./PrintableWorkout";
import WorkoutTemplateSidebar from "./WorkoutTemplateSidebar";
import WorkoutInfoSidebar from "./WorkoutInfoSidebar";

const WorkoutEditor = ({
    selectedDate,
    initialValues,
    onSave,
    isSaving,
    onDelete,
    onApplyTemplate
}) => {
    const { tenantSettings, tenantName, tenantLogo } = useTenant();
    const printRef = useRef(null);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

    const toggleTemplateModal = () => setIsTemplateModalOpen(!isTemplateModalOpen);
    const toggleInfoModal = () => setIsInfoModalOpen(!isInfoModalOpen);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: `Treino_${initialValues?.title || "Data"}`
    });

    if (!initialValues) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-swim"></i>
                        </div>
                    </div>
                    <h5>Selecione um treino</h5>
                    <p className="text-muted">Escolha um treino na lista ao lado ou crie um novo para esta data.</p>
                </div>
            </div>
        );
    }

    const calculateTotalDistance = (blocks) => {
        let total = 0;
        blocks?.forEach(block => {
            block.sets?.forEach(set => {
                total += (Number(set.reps) || 0) * (Number(set.distance) || 0);
            });
        });
        return total;
    };

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            <Formik
                initialValues={initialValues}
                validationSchema={workoutSchema}
                enableReinitialize={true}
                onSubmit={(values, { setSubmitting }) => {
                    onSave(values);
                    setSubmitting(false);
                }}
            >
                {({ values, errors, touched, handleChange, isSubmitting, setValues }) => (
                    <Form>
                        <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                    <div className="avatar-sm flex-shrink-0">
                                        <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                            <i className="mdi mdi-notebook-edit-outline font-size-22"></i>
                                        </span>
                                    </div>
                                    <div className="ms-3 overflow-hidden">
                                        <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                            <span className="text-truncate">{values.id ? "Editar Treino" : "Novo Treino"}</span>
                                            {values.id && (
                                                <span className="badge bg-soft-primary text-primary pill font-size-10 px-2 flex-shrink-0">
                                                    {selectedDate?.split("-").reverse().join("/")}
                                                </span>
                                            )}
                                        </h5>
                                        <p className="text-muted mb-0 small text-truncate">
                                            Volume Total: <strong className="text-primary">{calculateTotalDistance(values.blocks)}m</strong>
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2 flex-shrink-0 align-items-center">
                                    <span 
                                        className="text-primary d-flex align-items-center justify-content-center"
                                        style={{ width: '38px', height: '38px', cursor: 'pointer' }}
                                        onClick={toggleInfoModal}
                                        title="Zonas de Treinamento e Dicas"
                                    >
                                        <i className="mdi mdi-information-outline font-size-22"></i>
                                    </span>

                                    {!values.id && (
                                        <Button
                                            type="button"
                                            color="info"
                                            outline
                                            className="btn-md rounded-0 px-3 shadow-none bg-info-subtle border-0"
                                            onClick={toggleTemplateModal}
                                            title="Explorar Treinos Modelo"
                                        >
                                            <i className="mdi mdi-auto-fix font-size-16 me-1"></i> Usar Modelo
                                        </Button>
                                    )}

                                    <Button
                                        type="button"
                                        color="primary"
                                        className="d-flex align-items-center justify-content-center rounded-0 shadow-sm p-0 border-0"
                                        style={{ width: '38px', height: '38px' }}
                                        onClick={handlePrint}
                                        disabled={isSaving}
                                        title="Imprimir Treino"
                                    >
                                        <i className="mdi mdi-printer font-size-18"></i>
                                    </Button>

                                    {values.id && (
                                        <Button
                                            type="button"
                                            color="light"
                                            className="btn-md rounded-0 px-2 shadow-none text-danger border-0 d-flex align-items-center justify-content-center"
                                            style={{ width: '38px', height: '38px' }}
                                            onClick={() => onDelete(values.id, values.title)}
                                            disabled={isSaving}
                                            title="Excluir Treino"
                                        >
                                            <i className="mdi mdi-trash-can-outline font-size-18"></i>
                                        </Button>
                                    )}
                                    <Button
                                        color="primary"
                                        type="submit"
                                        className="btn-md rounded-0 px-4 shadow-sm"
                                        disabled={isSubmitting || isSaving}
                                    >
                                        {isSaving ? (
                                            <><Spinner size="sm" className="me-2" /> Salvando...</>
                                        ) : (
                                            <>Salvar</>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody className="p-4 bg-light-subtle pb-5">
                            <OverlayLoader show={isSaving} label="Salvando..." />
                            <Row className="mb-4">
                                <Col md={8}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-medium text-muted small text-uppercase">Título do Treino <span className="text-danger">*</span></Label>
                                        <Input
                                            id="title"
                                            name="title"
                                            type="text"
                                            className="form-control bg-white border"
                                            onChange={handleChange}
                                            value={values.title}
                                            invalid={!!(touched.title && errors.title)}
                                            placeholder="Ex: Treino Resistência Adulto"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-medium text-muted small text-uppercase">Data Planejada</Label>
                                        <Input
                                            id="date"
                                            name="date"
                                            type="date"
                                            className="form-control bg-white border"
                                            onChange={handleChange}
                                            value={values.date}
                                            invalid={!!(touched.date && errors.date)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={12}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-medium text-muted small text-uppercase">Objetivos / Observações gerais</Label>
                                        <Input
                                            id="description"
                                            name="description"
                                            type="textarea"
                                            rows={2}
                                            className="form-control bg-white border"
                                            onChange={handleChange}
                                            value={values.description}
                                            placeholder="Digite os objetivos deste treino..."
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FieldArray name="blocks">
                                {({ push, remove }) => (
                                    <div className="d-flex flex-column gap-3">
                                        {values.blocks.map((block, blockIndex) => (
                                            <Card key={block.id || blockIndex} className="border-0 shadow-sm rounded-0 mb-0 overflow-hidden">
                                                <div className="bg-primary bg-gradient position-absolute" style={{ left: 0, top: 0, bottom: 0, width: '4px' }}></div>
                                                <CardBody className="p-3 ps-4">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="mdi mdi-bookmark text-primary font-size-18"></i>
                                                            <Input
                                                                type="text"
                                                                name={`blocks.${blockIndex}.name`}
                                                                value={block.name}
                                                                onChange={handleChange}
                                                                className="border-0 fw-bold p-0 font-size-15 w-auto bg-transparent focus-ring-0 shadow-none"
                                                                style={{ minWidth: "200px" }}
                                                                placeholder="Nome do Bloco (Aquece...)"
                                                            />
                                                        </div>
                                                        <Button
                                                            color="danger"
                                                            outline
                                                            size="sm"
                                                            onClick={() => remove(blockIndex)}
                                                            className="border-0 rounded"
                                                        >
                                                            <i className="mdi mdi-delete-outline"></i>
                                                        </Button>
                                                    </div>

                                                    <FieldArray name={`blocks.${blockIndex}.sets`}>
                                                        {(setArrayHelpers) => (
                                                            <div className="d-flex flex-column gap-2 mb-2">
                                                                {block.sets.map((set, setIndex) => (
                                                                    <Row key={set.id || setIndex} className="align-items-center bg-light rounded-0 p-2 mx-0 gx-2 border hover-shadow transition-all">
                                                                        <Col md={1} xs={3}>
                                                                            <Label className="font-size-10 mb-1 text-muted fw-bold d-block text-truncate">Séries</Label>
                                                                            <Input type="number" name={`blocks.${blockIndex}.sets.${setIndex}.reps`} value={set.reps} onChange={handleChange} bsSize="sm" min="1" className="text-center fw-bold" />
                                                                        </Col>
                                                                        <Col md="auto" xs={1} className="text-center px-0">
                                                                            <span className="font-weight-bold text-muted fw-bold font-size-12 pt-4 d-inline-block">X</span>
                                                                        </Col>
                                                                        <Col md={2} xs={4}>
                                                                            <Label className="font-size-10 mb-1 text-muted fw-bold d-block text-truncate">Metragem</Label>
                                                                            <Input type="number" name={`blocks.${blockIndex}.sets.${setIndex}.distance`} value={set.distance} onChange={handleChange} bsSize="sm" min="0" className="text-primary fw-bold text-center" />
                                                                        </Col>
                                                                        <Col md={2} xs={4}>
                                                                            <Label className="font-size-10 mb-1 text-muted fw-bold d-block text-truncate">Estilo</Label>
                                                                            <Input type="select" name={`blocks.${blockIndex}.sets.${setIndex}.style`} value={set.style} onChange={handleChange} bsSize="sm" className="font-size-12">
                                                                                <option value="">Selecione...</option>
                                                                                {WORKOUT_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
                                                                            </Input>
                                                                        </Col>
                                                                        <Col md={2} xs={6} className="mt-2 mt-md-0">
                                                                            <Label className="font-size-10 mb-1 text-muted fw-bold d-block text-truncate">Intensidade</Label>
                                                                            <Input type="select" name={`blocks.${blockIndex}.sets.${setIndex}.intensity`} value={set.intensity} onChange={handleChange} bsSize="sm" className="font-size-12">
                                                                                <option value="">Zona Alvo</option>
                                                                                {WORKOUT_INTENSITIES.map(i => <option key={i} value={i}>{i}</option>)}
                                                                            </Input>
                                                                        </Col>
                                                                        <Col md={2} xs={6} className="mt-2 mt-md-0">
                                                                            <Label className="font-size-10 mb-1 text-muted fw-bold d-block text-truncate">Intervalo/Saída</Label>
                                                                            <Input type="text" name={`blocks.${blockIndex}.sets.${setIndex}.interval`} value={set.interval} onChange={handleChange} bsSize="sm" placeholder="ex: 1:30 ou 15''" className="font-size-12" />
                                                                        </Col>
                                                                        <Col md={2} xs={10} className="mt-2 mt-md-0">
                                                                            <Label className="font-size-10 mb-1 text-muted fw-bold d-block text-truncate">Materiais/Obs</Label>
                                                                            <Input type="text" name={`blocks.${blockIndex}.sets.${setIndex}.description`} value={set.description} onChange={handleChange} bsSize="sm" placeholder="ex: palmar" className="font-size-12" />
                                                                        </Col>
                                                                        <Col md="auto" xs={2} className="text-center mt-2 mt-md-0 px-1 d-flex align-items-end justify-content-center">
                                                                            <Button type="button" outline color="danger" size="sm" onClick={() => setArrayHelpers.remove(setIndex)} className="border-0 px-2 w-100" style={{ height: '29px' }}>
                                                                                <i className="mdi mdi-close"></i>
                                                                            </Button>
                                                                        </Col>
                                                                    </Row>
                                                                ))}
                                                                <Button
                                                                    type="button"
                                                                    outline
                                                                    color="primary"
                                                                    size="sm"
                                                                    className="mt-2 border-dashed bg-white"
                                                                    onClick={() => setArrayHelpers.push({ id: Date.now().toString(), reps: 1, distance: 50, style: "", intensity: "", interval: "", description: "" })}
                                                                >
                                                                    <i className="mdi mdi-plus mr-1"></i> Linha de Série
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </FieldArray>
                                                </CardBody>
                                            </Card>
                                        ))}

                                        <div className="text-center mt-4 mb-2">
                                            <Button
                                                type="button"
                                                color="light"
                                                className="px-4 py-2 fw-bold text-primary shadow-sm rounded-0"
                                                onClick={() => push({ id: Date.now().toString(), name: "Novo Bloco", sets: [] })}
                                            >
                                                <i className="mdi mdi-view-grid-plus outline font-size-16 align-middle me-2"></i>
                                                Adicionar Novo Bloco (Ex: Soltura)
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </FieldArray>
                        </CardBody>
                        
                        {/* Relatório oculto para impressão via react-to-print */}
                        <div style={{ display: "none" }}>
                            <div ref={printRef}>
                                <PrintableWorkout
                                    workout={values}
                                    tenantName={tenantName || tenantSettings?.name}
                                    tenantLogo={tenantLogo || tenantSettings?.logo}
                                />
                            </div>
                        </div>

                        <WorkoutTemplateSidebar
                            isOpen={isTemplateModalOpen}
                            toggle={toggleTemplateModal}
                            selectedDate={selectedDate}
                            onSelectTemplate={(template) => {
                                setValues({
                                    ...values, // preserva id, currDate, etc.
                                    title: template.title,
                                    description: template.description || "",
                                    blocks: template.blocks || []
                                });
                            }}
                        />

                        <WorkoutInfoSidebar
                            isOpen={isInfoModalOpen}
                            toggle={toggleInfoModal}
                        />
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

export default WorkoutEditor;
